import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient as api } from '../../lib/apiClient';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Clock, User as UserIcon, Tag, Info } from 'lucide-react';

interface Log {
  id: number;
  order_id: number | null;
  action: string;
  details: string | null;
  created_at: string;
  user: {
    name: string;
  } | null;
  order?: {
    table?: {
      number: number;
    }
  }
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  CREATED: { label: 'Nueva Orden', color: 'bg-blue-100 text-blue-700' },
  PAID: { label: 'Pago Marcado', color: 'bg-emerald-100 text-emerald-700' },
  CHARGED: { label: 'Cobro Exitoso', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelación', color: 'bg-red-100 text-red-700' },
  ITEMS_ADDED: { label: 'Items Agregados', color: 'bg-purple-100 text-purple-700' },
  SPLIT: { label: 'División', color: 'bg-orange-100 text-orange-700' },
  CREATED_FROM_SPLIT: { label: 'Nueva (Dividida)', color: 'bg-amber-100 text-amber-700' },
};

export function ActivityLogs() {
  const { data: logs, isLoading } = useQuery<Log[]>({
    queryKey: ['activity-logs'],
    queryFn: async () => {
      const res = await api.get('/dashboard/activity-logs');
      return res.data;
    },
    refetchInterval: 10000, // Refrescar cada 10s
  });

  if (isLoading) {
    return <div className="p-4 text-center text-muted">Cargando historial...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text">Movimientos en Tiempo Real</h2>
      </div>

      <div className="rounded-2xl border border-divider bg-surface overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface-muted text-xs font-semibold uppercase tracking-wider text-muted">
              <tr>
                <th className="px-5 py-3">Evento</th>
                <th className="px-5 py-3">Detalles</th>
                <th className="px-5 py-3">Responsable</th>
                <th className="px-5 py-3 text-right">Fecha/Hora</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {logs?.map((log) => {
                const config = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700' };
                return (
                  <tr key={log.id} className="hover:bg-surface-muted/50 transition-colors">
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color}`}>
                        {config.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-text">
                          {log.details || 'Sin detalles'}
                        </span>
                        {log.order?.table && (
                          <span className="text-xs text-muted">Mesa #{log.order.table.number}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-muted">
                        <UserIcon size={14} />
                        <span>{log.user?.name || 'Sistema'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-text font-medium">
                          {format(new Date(log.created_at), 'HH:mm', { locale: es })}
                        </span>
                        <span className="text-[10px] text-muted">
                          {format(new Date(log.created_at), "d 'de' MMM", { locale: es })}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
